import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";
import Brand from "@/components/Brand";
import { betaApply } from "@/lib/api";
import { AGREEMENT, AGREEMENT_INTRO, AGREEMENT_VERSION, SETUP_OPTIONS } from "@/lib/beta-agreement";

/**
 * Beta sign-up, acars.flyvac.org/apply (2026-10-11). Public: an applicant has
 * no session, because the closed beta refuses non-testers at sign-in. They
 * type their usual credentials here, which are checked on every call and kept
 * only in this page's memory (never stored), then fill in the form and accept
 * the agreement. backend/beta_apply.py has the rest.
 */

const DOWNLOAD_URL = "https://myvac.flyvac.org/downloads/vacars5/";
const errText = (e, fallback) => e?.response?.data?.detail
  ? (typeof e.response.data.detail === "string" ? e.response.data.detail : fallback)
  : fallback;
const day = (s) => new Date(Number(s) * 1000).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

function Shell({ children }) {
  return (
    <div className="min-h-screen text-foreground flex justify-center px-4 py-10" data-testid="apply-page">
      <div className="w-full max-w-2xl">
        <div className="mb-8"><Brand /></div>
        {children}
      </div>
    </div>
  );
}

function Agreement() {
  return (
    <div className="max-h-80 overflow-y-auto border border-border rounded-md p-4 text-sm leading-relaxed bg-background/50"
      data-testid="apply-agreement" tabIndex={0}>
      <p className="mb-3">{AGREEMENT_INTRO}</p>
      {AGREEMENT.map((s) => (
        <section key={s.h} className="mb-3">
          <h3 className="font-bold mb-1">{s.h}</h3>
          {s.body.map((b, i) => Array.isArray(b)
            ? <ul key={i} className="list-disc pl-5 mb-2 space-y-1">{b.map((li) => <li key={li}>{li}</li>)}</ul>
            : <p key={i} className="mb-2">{b}</p>)}
        </section>
      ))}
    </div>
  );
}

function SignIn({ onDone }) {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      onDone(await betaApply.status(login, password), { login, password });
    } catch (e2) {
      setErr(errText(e2, "Couldn't check that right now. Try again in a minute."));
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="card-flat p-6" data-testid="apply-signin">
      <div className="overline mb-2">Step 1 of 2</div>
      <h2 className="text-xl font-bold mb-1">Sign in to apply</h2>
      <p className="text-sm text-muted-foreground mb-5">
        Use your usual FlyVAC pilot ID and password, so we know the application is yours.
      </p>
      <label className="block mb-4">
        <span className="overline block mb-1.5">Pilot ID or email</span>
        <input required autoFocus value={login} onChange={(e) => setLogin(e.target.value)}
          placeholder="e.g. 44568 or VAC044568" className="field w-full px-3 py-2 text-sm font-mono" data-testid="apply-login" />
      </label>
      <label className="block mb-5">
        <span className="overline block mb-1.5">Password</span>
        <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          className="field w-full px-3 py-2 text-sm font-mono" data-testid="apply-password" />
      </label>
      {err && <div className="mb-4 rounded-md text-xs text-primary border border-primary/50 bg-primary/10 px-3 py-2" data-testid="apply-error">{err}</div>}
      <button disabled={busy} className="w-full rounded-md bg-primary text-primary-foreground text-sm font-bold uppercase tracking-widest py-3 hover:brightness-110 disabled:opacity-60 flex items-center justify-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Continue
      </button>
    </form>
  );
}

function ApplyForm({ who, creds, onDone, again }) {
  const [f, setF] = useState({ setup: "", sim_version: "", discord: "", notes: "", agree: false,
    signed_name: "", adult: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const ready = f.setup && f.sim_version.trim() && f.discord.trim() && f.adult && f.agree
    && f.signed_name.trim().length >= 3;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      onDone(await betaApply.send({ ...creds, ...f, notes: f.notes || null, agreement_version: AGREEMENT_VERSION }));
    } catch (e2) {
      setErr(errText(e2, "Couldn't send it right now. Your answers are still here; try again in a minute."));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="card-flat p-6 space-y-6" data-testid="apply-form">
      <div>
        <div className="overline mb-2">Step 2 of 2</div>
        <h2 className="text-xl font-bold mb-1">Hi {who.first_name || who.pid}, tell us about your setup</h2>
        <p className="text-sm text-muted-foreground">
          Applying as <span className="font-mono">{who.pid}</span>.
          {again && " Your last application wasn't accepted, but you're welcome to apply again."}
        </p>
      </div>

      <fieldset>
        <legend className="overline mb-2">Your computer and sim</legend>
        <div className="grid sm:grid-cols-3 gap-2">
          {SETUP_OPTIONS.map((o) => (
            <label key={o.id} className={`border rounded-md p-3 cursor-pointer text-sm transition-colors ${f.setup === o.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/60"}`}>
              <input type="radio" name="setup" value={o.id} checked={f.setup === o.id} onChange={set("setup")} className="sr-only" />
              <div className="font-semibold">{o.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{o.hint}</div>
            </label>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">MSFS doesn't run on a Mac, so Mac testers fly X-Plane.</p>
      </fieldset>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="overline block mb-1.5">Sim and version</span>
          <input value={f.sim_version} onChange={set("sim_version")} maxLength={64} placeholder="e.g. MSFS 2024"
            className="field w-full px-3 py-2 text-sm" data-testid="apply-sim" />
        </label>
        <label className="block">
          <span className="overline block mb-1.5">Discord username</span>
          <input value={f.discord} onChange={set("discord")} maxLength={64} placeholder="So we can add you to the beta channel"
            className="field w-full px-3 py-2 text-sm" data-testid="apply-discord" />
        </label>
      </div>

      <label className="block">
        <span className="overline block mb-1.5">Anything we should know? (optional)</span>
        <textarea value={f.notes} onChange={set("notes")} maxLength={2000} rows={3}
          placeholder="Aircraft you fly most, how often you fly, other add-ons…" className="field w-full px-3 py-2 text-sm" />
      </label>

      <div>
        <div className="overline mb-2">The Beta Tester Agreement</div>
        <Agreement />
        <label className="flex items-start gap-2 mt-3 text-sm">
          <input type="checkbox" checked={f.adult} onChange={set("adult")} className="mt-1" data-testid="apply-adult" />
          <span>I confirm I'm 18 or older.</span>
        </label>
        <label className="flex items-start gap-2 mt-2 text-sm">
          <input type="checkbox" checked={f.agree} onChange={set("agree")} className="mt-1" data-testid="apply-agree" />
          <span>I've read the agreement and I agree to it.</span>
        </label>
        <label className="block mt-3">
          <span className="overline block mb-1.5">Type your full name to sign</span>
          <input value={f.signed_name} onChange={set("signed_name")} maxLength={128} autoComplete="name"
            className="field w-full px-3 py-2 text-sm" data-testid="apply-signed-name" />
        </label>

      </div>

      <p className="text-xs text-muted-foreground">
        Your application (pilot ID, setup, sim, Discord username, notes and the name typed above) is stored on
        FlyVAC's server for FlyVAC admins. Your pilot ID, setup, sim and Discord username are also posted to a
        private staff channel so we see it quickly.
      </p>

      {err && <div className="rounded-md text-xs text-primary border border-primary/50 bg-primary/10 px-3 py-2" data-testid="apply-error">{err}</div>}
      <button disabled={busy || !ready} data-testid="apply-submit"
        className="w-full rounded-md bg-primary text-primary-foreground text-sm font-bold uppercase tracking-widest py-3 hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send application
      </button>
    </form>
  );
}

function Outcome({ who }) {
  const app = who.application;
  if (who.status === "tester") {
    return (
      <div className="card-flat p-8 text-center" data-testid="apply-tester">
        <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-emerald-500" />
        <h2 className="text-xl font-bold mb-2">You're in the beta</h2>
        <p className="text-sm text-muted-foreground mb-6">
          Download VACARS5, sign in with your usual pilot ID and password, and look for the Beta test page in the sidebar.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <a href={DOWNLOAD_URL} target="_blank" rel="noopener noreferrer" className="text-xs bg-primary text-primary-foreground px-4 py-2 rounded-md font-mono uppercase tracking-wider hover:brightness-110">Download VACARS5</a>
          <Link to="/login" className="text-xs border border-border px-4 py-2 rounded-md font-mono uppercase tracking-wider hover:border-primary">Sign in on the web</Link>
        </div>
      </div>
    );
  }
  return (
    <div className="card-flat p-8 text-center" data-testid="apply-pending">
      <Clock className="w-10 h-10 mx-auto mb-3 text-primary" />
      <h2 className="text-xl font-bold mb-2">Application received</h2>
      <p className="text-sm text-muted-foreground">
        {app ? `Sent ${day(app.created_at)} for ${app.setup_label}. ` : ""}
        The team will review it and message you on Discord. Once you're approved you'll be able to sign in to VACARS5
        and the new myVAC with your usual pilot ID and password.
      </p>
    </div>
  );
}

export default function Apply() {
  const [who, setWho] = useState(null);
  const [creds, setCreds] = useState(null);
  const [again, setAgain] = useState(false);
  // Admins close sign-up between betas (2026-10-15). Unknown (still loading, or
  // the server unreachable) shows the form; the server refuses a closed apply.
  const { data: gate } = useQuery({ queryKey: ["beta-apply-open"], queryFn: betaApply.open, staleTime: 60_000 });
  const closed = gate && gate.open === false;

  return (
    <Shell>
      <div className="mb-6">
        <div className="overline mb-1">VACARS5 closed beta</div>
        <h1 className="text-3xl font-bold tracking-tight">Apply to be a beta tester</h1>
        <div className="acbar" />
        <p className="text-sm text-muted-foreground mt-4">
          Open to FlyVAC pilots aged 18 or older. Fly real FlyVAC flights with VACARS5 before it's released, work through a short checklist as you fly, and
          tell us what breaks. It takes about 15 minutes on top of a normal flight.
        </p>
      </div>

      {closed && (
        <div className="card-flat p-8 text-center" data-testid="apply-closed">
          <Clock className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
          <h2 className="text-xl font-bold mb-2">Applications are closed</h2>
          <p className="text-sm text-muted-foreground">
            {gate.message || "We're not taking new beta testers right now. Watch the FlyVAC Discord for the next beta."}
          </p>
        </div>
      )}
      {!closed && !who && <SignIn onDone={(w, c) => { setWho(w); setCreds(c); }} />}
      {who && who.status === "declined" && !again && (
        <div className="card-flat p-8 text-center" data-testid="apply-declined">
          <XCircle className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
          <h2 className="text-xl font-bold mb-2">Not this time</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Your last application wasn't accepted. That's usually because we have enough testers with your setup for now.
          </p>
          <button onClick={() => setAgain(true)} className="text-xs border border-border px-4 py-2 rounded-md font-mono uppercase tracking-wider hover:border-primary">Apply again</button>
        </div>
      )}
      {who && (who.status === "none" || (who.status === "declined" && again)) && (
        <ApplyForm who={who} creds={creds} again={again} onDone={(w) => { setWho(w); setCreds(null); }} />
      )}
      {who && (who.status === "pending" || who.status === "tester") && <Outcome who={who} />}

      <p className="text-xs text-muted-foreground mt-8">
        Not applying? Keep flying with VACARS3 on vacanada.org; VACARS5 opens to everyone at launch.
      </p>
    </Shell>
  );
}
